import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useUserContext } from "../../context/UserContext";
import { useSweepstakesContext } from "../../context/SweepstakesContext";
import { postEmail } from "../../services/apiClient";
import { scrollTo, triggerFloodLightEvent, triggerGAEvent } from "../../helpers";

import Input from "../../common/Elements/Input";
import Button from "../../common/Elements/Button";
import InputError from "../../common/Elements/InputError";
import Video from "../../common/Video";

const EmailForm = () => {
  const { setEnteredPromo, quizResultId, setUserId, isTrade } = useUserContext();
  const { setUserEmail, setShowSection, setCaptcha, gaCodes } = useSweepstakesContext();

  const {
    register,
    handleSubmit,
    formState: { errors: formErrors, isSubmitting },
    setError
  } = useForm();

  const validation = {
    email: { required: "Email is required" }
  };

  const [submitting, setSubmitting] = useState(false);
  const [videosPlaying, setVideosPlaying] = useState(true);

  const handleVideo = () => {
    if (videosPlaying) {
      setVideosPlaying(false);
    } else {
      setVideosPlaying(true);
    }
  };

  const checkEmail = (data) => {
    if (!submitting) {
      data.gaCodes = gaCodes;
      data.quizResultId = quizResultId;
      data.is_trade = isTrade;

      triggerGAEvent("home_sweeps-continue");

      setSubmitting(true);

      postEmail(data)
        .then((response) => {
          if (response.status === 201) {
            // Entry completed successfully

            setShowSection(response.data.validEntry ? "thank-you" : "already-entered");
            if (response.data.validEntry) triggerGAEvent("entered");
            setUserId(response.data.userId);
            scrollTo("sweeps");
            setEnteredPromo(true);
          } else {
            // Something went wrong
            setError("email", { message: "Something went wrong. Please refresh try again." });
          }
          setSubmitting(false);
        })
        .catch((e) => {
          if (e.response) {
            const { response } = e;
            if (
              response.status === 422 &&
              response.data.errors &&
              Object.keys(response.data.errors).length > 0
            ) {
              // Backend validation errors
              Object.keys(response.data.errors).forEach((key) => {
                setError(key, { type: "server", message: response.data.errors[key][0] });
              });
            } else if (response.status === 409 && response.data.error === "email_not_found") {
              // No email found, show registration form
              setUserEmail(data.email);
              setCaptcha(response.data.captcha);
              scrollTo("sweeps");
              setShowSection("registration");

              if (!isTrade) {
                triggerFloodLightEvent("carni046+unique");
                document.getElementById("ttd-pixel").innerHTML =
                  '<img height="1" width="1" style="border-style:none;" alt="" src="https://insight.adsrvr.org/track/pxl/?adv=trl7bi1&ct=0:rtnffuk&fmt=3"/>';
              }
            } else {
              // Something went wrong
              setError("email", { message: "Something went wrong. Please refresh try again." });
            }
          } else {
            setError("email", { message: "Something went wrong. Please refresh try again." });
          }
          setSubmitting(false);
        });
    }
  };

  return (
    <div className="lg:flex lg:justify-center  lg:h-[400px] relative">
      <div className="hidden lg:block w-full max-w-[400px] xl:w-[800px] overflow-hidden">
        <Video name={`left`} autoplay={videosPlaying} />
      </div>
      <div className="pt-8 p-3 lg:p-6 w-full lg:h-full flex items-center  justify-center">
        <div className="relative z-10">
          <div>
            {" "}
            <h2
              id="sweeps-headline"
              className="my-0 text-center text-[24px] lg:text-[32px] lg:leading-[35px] text-dark-blue leading-tight"
            >
              <span className="lg:block">A new chance to win is now&nbsp;unlocked!</span>
            </h2>
            <p className="text-[19px] lg:leading-[25px] leading-tight py-4 max-w-[472px] mx-auto text-dark-blue px-3">
              Enter now and you could win a Carnival cruise that sails to Celebration&nbsp;Key™
              in&nbsp;2025.
            </p>
          </div>

          <form onSubmit={handleSubmit(checkEmail)} className="px-4">
            <label htmlFor="email" className="sr-only">
              Email
            </label>
            <div className="lg:flex">
              <Input
                id="email"
                type="email"
                placeholder="Email"
                aria-describedby="sweeps-headline"
                aria-required="true"
                aria-invalid={formErrors.email ? "true" : "false"}
                auto-complete="email"
                {...register("email", validation.email)}
                className={formErrors.email ? `border-red` : `border-[#0680B5]`}
              />
              <Button
                disabled={isSubmitting}
                as="submit"
                className="w-full my-2 lg:my-0 lg:max-w-[162px] px-0"
              >
                Continue
              </Button>
            </div>
            <div className="h-4">
              <InputError className="text-left">
                {formErrors.email && formErrors.email.message}
              </InputError>
            </div>
          </form>
        </div>
      </div>
      <div
        className="hidden lg:block w-full max-w-[400px] xl:w-[800px] h-full overflow-hidden"
        style={{ height: `inherit` }}
      >
        <div
          tabIndex={0}
          role="button"
          onKeyDown={() => handleVideo()}
          onClick={() => handleVideo()}
          aria-label={(videosPlaying ? "Pause" : "Play") + " all videos"}
          className={"absolute top-0 right-0 hidden lg:block z-50 bg-dark-blue px-1"}
        >
          {!videosPlaying && (
            <span
              aria-hidden
              className="material-symbols-rounded filled opacity-100 hover:opacity-75 text-4xl lg:text-4xl text-white"
            >
              play_arrow
            </span>
          )}
          {videosPlaying && (
            <span className="material-symbols-rounded filled opacity-100 hover:opacity-75 text-4xl lg:text-4xl text-white">
              pause
            </span>
          )}
        </div>
        <Video name={`right`} autoplay={videosPlaying} />
      </div>
      <div
        className="block lg:hidden h-[200px] overflow-hidden relative"
        style={{ height: `inherit` }}
      >
        <div
          tabIndex={0}
          role="button"
          onKeyDown={() => handleVideo()}
          onClick={() => handleVideo()}
          aria-label={(videosPlaying ? "Pause" : "Play") + " all videos"}
          className={"absolute top-0 right-0 block lg:hidden bg-dark-blue z-50 px-1"}
        >
          {!videosPlaying && (
            <span
              aria-hidden
              className="material-symbols-rounded filled opacity-100 hover:opacity-75 text-4xl lg:text-5xl text-white"
            >
              play_arrow
            </span>
          )}
          {videosPlaying && (
            <span className="material-symbols-rounded filled opacity-100 hover:opacity-75 text-4xl lg:text-5xl text-white">
              pause
            </span>
          )}
        </div>
        <div className="absolute w-full h-[100px] top-0 lg:hidden bg-gradient-to-b from-lightest-blue opacity-100" />
        <Video name={`left`} autoplay={videosPlaying} />
      </div>
    </div>
  );
};

export default EmailForm;
